// src/components/JobRequests.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigation } from '../contexts/NavigationContext';
import { formatDate } from '../utils/formatHelpers';
import { CheckCircle, Cancel, HourglassEmpty } from '@mui/icons-material';

function JobRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { navigateToCreateJobWithData } = useNavigation();

  useEffect(() => {
    const q = query(collection(db, 'jobRequests'), where('status', '==', 'pending'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRequests(requestsList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching job requests:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleApprove = (request) => {
    // Map request data to what CreateJob component expects
    const jobData = {
      customerName: request.customerName,
      jobTemplateName: request.serviceType,
      vehicleReg: request.vehicleRegistration,
      vehicleMake: request.vehicleMake,
      vehicleModel: request.vehicleModel,
      siteAddress: request.location,
      adminNotes: `From customer request: ${request.description}`, // Pre-fill notes
      // Pass the request ID to delete it upon successful job creation
      sourceRequestId: request.id, 
    };
    navigateToCreateJobWithData(jobData);
  };

  const handleReject = async (requestId) => {
    const requestRef = doc(db, 'jobRequests', requestId);
    try {
      // We'll update the status to 'rejected' so it's removed from this list
      // but can be reviewed later if needed.
      await updateDoc(requestRef, { status: 'rejected' });
    } catch (error) {
      console.error("Error rejecting request:", error);
      // You might want to show a snackbar error here
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
          <HourglassEmpty sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No pending job requests
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {requests.map((request) => (
            <Card key={request.id}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" fontWeight="bold">{request.serviceType}</Typography>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      From: {request.customerName}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2"><strong>Vehicle:</strong> {request.vehicleRegistration || 'N/A'} ({request.vehicleMake} {request.vehicleModel})</Typography>
                    <Typography variant="body2"><strong>Location:</strong> {request.location}</Typography>
                    {request.description && <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}><strong>Notes:</strong> {request.description}</Typography>}
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Requested: {request.createdAt ? formatDate(request.createdAt) : 'N/A'}
                    </Typography>
                    {request.preferredDate && <Typography variant="body2" color="text.secondary">Preferred Date: {request.preferredDate}</Typography>}
                    {request.preferredTime && <Typography variant="body2" color="text.secondary">Preferred Time: {request.preferredTime}</Typography>}
                    {request.isUrgent && <Chip label="Urgent" color="error" size="small" sx={{ mt: 1 }} />}
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', p: 2, bgcolor: 'action.hover' }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleReject(request.id)}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                  onClick={() => handleApprove(request)}
                >
                  Approve & Create Job
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default JobRequests;